import React, { useContext, useState } from 'react';
import { Page, useFetch, PermissionGuard } from '@mss/mss-component-library';
import Breadcrumbs from '../../../../common/Breadcrumbs';
import EditSidebar from './Sidebar';
import {
  Switch,
  Route,
  Redirect,
  useRouteMatch,
  useParams,
  useHistory,
} from 'react-router-dom';
import { notification } from 'antd';
import Permissions from './Permissions';
import Button from '../../../../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan } from '@fortawesome/free-solid-svg-icons';
import emptyGroup from '../../../util/emptyGroup';
import Members from './Members';
import Settings from './Settings';
import DeleteGroupModal from '../Delete/Modal';
import { useTranslation } from 'react-i18next';
import ObjectPermissions from '../../Users/Edit/ObjectPermissions';
import isSuperAdmin from 'jsx/authorization/util/isSystemGroup';
import { AppContext } from 'jsx';
import { useQueryClient } from '@tanstack/react-query';
import { sanitizedUrl } from '../../../../asset/util/sanitize';

export default function Edit() {
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const { groupId = '' } = useParams<{ groupId: string }>();
  const validId = !isNaN(parseInt(groupId));
  const [t] = useTranslation(['authorization', 'asset', 'common']);
  const { invertedAllPermissionsMap } = useContext(AppContext);
  const [checkedList, setCheckedList] = useState<(number | undefined)[]>([]);
  const [groupPermissions, setGroupPermissions] = useState<
    (number | undefined)[]
  >([]);
  const queryClient = useQueryClient();
  const [fetchedGroup, fetchGroup] = useFetch({
    url: `/api/group/${groupId}/`,
    defaultValue: emptyGroup,
    onSucces: ({ data }) => {
      setCheckedList(data.permissions);
      setGroupPermissions(data.permissions);
    },
    onEmpty: ({ data }) => {
      setCheckedList(data.permissions);
      setGroupPermissions(data.permissions);
    },
    onFail: () => {
      notification.error({
        message: t('Failed to get group information'),
        description: !validId
          ? t(
              'It seems there is an invalid group id in the url. Please navigate to the overview page and click on the asset there.'
            )
          : t(
              'We could not retrieve the data for this group. Please try again later.'
            ),
      });
    },
  });

  const superAdminGroup: boolean = isSuperAdmin(fetchedGroup.data);

  return (
    <>
      <Page
        breadCrumbs={
          <Breadcrumbs
            label={
              fetchedGroup.loading
                ? `${t('Loading')}...`
                : `${fetchedGroup.data.name}`
            }
          />
        }
        actions={
          <PermissionGuard codename={'delete_group'}>
            <Button
              disabled={superAdminGroup}
              onClick={() => {
                DeleteGroupModal(fetchedGroup.data, t, queryClient, () =>
                  history.push('/authorization/groups')
                );
              }}
              data-ta="deleteGroupButton"
            >
              <FontAwesomeIcon icon={faBan} className="mr-1" />
              {t('DELETE GROUP')}
            </Button>
          </PermissionGuard>
        }
        main={
          <Switch>
            <Route
              path={`${path}/objectpermissions`}
              render={() =>
                superAdminGroup ? (
                  <Redirect to={`${path}`} />
                ) : (
                  <ObjectPermissions
                    isGroup
                    userPermissions={fetchedGroup.data.permissions.map(
                      (permission_id: number) => ({
                        permission_id,
                        codename:
                          invertedAllPermissionsMap.get(permission_id) || '',
                      })
                    )}
                    superAdminGroup={superAdminGroup}
                  />
                )
              }
            />

            <Route
              path={`${path}/permissions`}
              render={() => (
                <Permissions
                  group={fetchedGroup.data}
                  groupLoading={fetchedGroup.loading}
                  groupPermissions={groupPermissions}
                  fetchGroupPermissions={fetchGroup}
                  superAdminGroup={superAdminGroup}
                  checkedList={checkedList}
                  setCheckedList={setCheckedList}
                />
              )}
            />
            <Route
              path={`${path}/members`}
              render={() => <Members superAdminGroup={superAdminGroup} />}
            />
            <Route
              path={`${path}/settings`}
              render={() => (
                <Settings
                  group={fetchedGroup.data}
                  superAdminGroup={superAdminGroup}
                  fetchGroup={fetchGroup}
                />
              )}
            />

            <Redirect to={`${sanitizedUrl(url)}/settings`} />
          </Switch>
        }
        sidebar={<EditSidebar superAdminGroup={superAdminGroup} />}
      />
    </>
  );
}
